import { useEffect, useState, type MouseEvent } from 'react'
import { cn } from '@/lib/utils'
import { DriveImportPickerTrigger } from './drive-import-picker'
import {
  DriveFolderJumpTree,
  type DriveFolderJump,
} from './drive-folder-jump-tree'
import {
  DRIVE_BROWSE_SCOPE_EVENT,
  dispatchDriveBrowseScope,
  getLastDriveMainSurface,
  type DriveBrowseScopeDetail,
  type DriveBrowseScopeKind,
} from './drive-browse-events'

/**
 * Mode-panel Drive chrome: folder jump tree + Import.
 * Drive account focus (PIP) lives on DriveSidebar so it stays visible when
 * the focused workspace is not connected. Folder list is DriveFolderView.
 */
export function DriveFileTab({
  organizationId,
  onOpenImportPicker,
  onOpenFolderView,
  fill = true,
}: {
  dense?: boolean
  fill?: boolean
  organizationId?: string
  onAuthError?: () => void
  /** Open the Drive folder view in the main panel before the scope event. */
  onOpenFolderView?: () => void
  onOpenDrivePreview?: (
    file: {
      id: string
      name: string
      mimeType: string
      webViewLink?: string
      shortcutTarget?: { id: string; mimeType: string } | null
    },
    options: { organizationId: string; event?: MouseEvent; importedDocumentId?: string },
  ) => void
  onOpenImportPicker?: (opts?: { organizationId?: string }) => void
}) {
  const [workspaceId, setWorkspaceId] = useState(organizationId ?? '')
  const [viewKind, setViewKind] = useState<DriveBrowseScopeKind | 'sharedDrive'>('myDrive')
  const [folderId, setFolderId] = useState<string | undefined>()
  const [driveId, setDriveId] = useState<string | undefined>()

  useEffect(() => {
    if (organizationId) setWorkspaceId(organizationId)
  }, [organizationId])

  useEffect(() => {
    if (!workspaceId) return
    // Folder-tree workspace focus must not steal Activity / Linked mains.
    if (getLastDriveMainSurface() !== 'drive') return
    dispatchDriveBrowseScope({ workspaceId, viewKind: 'myDrive', source: 'tree' })
    setViewKind('myDrive')
    setFolderId(undefined)
    setDriveId(undefined)
  }, [workspaceId])

  useEffect(() => {
    const onScope = (event: Event) => {
      const detail = (event as CustomEvent<DriveBrowseScopeDetail>).detail
      if (!detail?.workspaceId || detail.workspaceId !== workspaceId) return
      setViewKind(detail.viewKind)
      setFolderId(detail.folderId)
      setDriveId(detail.driveId)
    }
    window.addEventListener(DRIVE_BROWSE_SCOPE_EVENT, onScope)
    return () => window.removeEventListener(DRIVE_BROWSE_SCOPE_EVENT, onScope)
  }, [workspaceId])

  const handleJump = (jump: DriveFolderJump) => {
    if (!workspaceId) return
    setViewKind(jump.viewKind)
    setFolderId(jump.folderId)
    setDriveId(jump.driveId)
    onOpenFolderView?.()
    dispatchDriveBrowseScope({ workspaceId, ...jump, source: 'tree' })
  }

  return (
    <div className={cn('flex min-h-0 flex-col', fill ? 'flex-1' : 'max-h-[45%] shrink-0')}>
      {onOpenImportPicker ? (
        <div className="shrink-0 space-y-2 px-2 pb-2">
          <DriveImportPickerTrigger
            onClick={() => onOpenImportPicker({ organizationId: workspaceId })}
            disabled={!workspaceId}
            className="w-full"
          />
        </div>
      ) : null}
      {workspaceId ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DriveFolderJumpTree
            workspaceId={workspaceId}
            currentFolderId={folderId}
            currentViewKind={viewKind}
            currentDriveId={driveId}
            onJump={handleJump}
          />
        </div>
      ) : null}
    </div>
  )
}
